import { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronDown, FiDownload, FiRefreshCw, FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";
import axiosInstance from "../axiosinstance";
import PaginationFooter from "../Components/PaginationFooter";
import "./EventParticipantsImport.css";

const getSelectedProjectId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return String(stored?.project_id || stored?.id || stored || "").trim();
  }
  catch { return null; }
};

const projectIdFromEvent = (event) => {
  const eventProjectId = event?.detail?.project_id;
  return eventProjectId == null
    ? getSelectedProjectId()
    : String(eventProjectId?.project_id || eventProjectId?.id || eventProjectId || "").trim();
};

export default function EventParticipantsImport({ isSidebarExpanded }) {
  const inputRef = useRef(null);
  const uploadControllerRef = useRef(null);
  const nfcMenuRef = useRef(null);
  const [projectId, setProjectId] = useState(getSelectedProjectId);
  const projectIdRef = useRef(projectId);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [nfcStatus, setNfcStatus] = useState("");
  const [isNfcMenuOpen, setIsNfcMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const syncProject = (event) => {
      const nextProjectId = projectIdFromEvent(event);
      projectIdRef.current = nextProjectId;
      uploadControllerRef.current?.abort();
      setProjectId(nextProjectId);
    };
    window.addEventListener("projectChanged", syncProject);
    window.addEventListener("storage", syncProject);
    return () => {
      window.removeEventListener("projectChanged", syncProject);
      window.removeEventListener("storage", syncProject);
    };
  }, []);

  useEffect(() => {
    projectIdRef.current = projectId;
    uploadControllerRef.current?.abort();
    setParticipants([]);
    setTotalRecords(0);
    setCurrentPage(1);
    setQuery("");
    setSearch("");
    setNfcStatus("");
    setFile(null);
    setResult(null);
    setShowImport(false);
    if (inputRef.current) inputRef.current.value = "";
  }, [projectId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setSearch(query.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const closeNfcMenu = (event) => {
      if (!nfcMenuRef.current?.contains(event.target)) setIsNfcMenuOpen(false);
    };
    window.addEventListener("mousedown", closeNfcMenu);
    return () => window.removeEventListener("mousedown", closeNfcMenu);
  }, []);

  const loadParticipants = useCallback(async (signal) => {
    if (!projectId) {
      setParticipants([]);
      setTotalRecords(0);
      setLoadError("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    const requestedProjectId = projectId;
    try {
      const response = await axiosInstance.get(
        `/api/projects/${encodeURIComponent(projectId)}/event_participants`,
        { params: { page: currentPage, page_size: itemsPerPage, search, ...(nfcStatus ? { nfc_configured: nfcStatus } : {}) }, signal },
      );
      if (projectIdRef.current !== requestedProjectId) return;
      setParticipants(response.data?.participants || []);
      setTotalRecords(response.data?.pagination?.total_records || 0);
    } catch (error) {
      if (error?.code !== "ERR_CANCELED" && projectIdRef.current === requestedProjectId) {
        setParticipants([]);
        setTotalRecords(0);
        setLoadError(error.response?.data?.message || "Unable to load event participants.");
      }
    } finally {
      if (!signal?.aborted && projectIdRef.current === requestedProjectId) setLoading(false);
    }
  }, [projectId, currentPage, itemsPerPage, search, nfcStatus]);

  useEffect(() => {
    const controller = new AbortController();
    loadParticipants(controller.signal);
    return () => controller.abort();
  }, [loadParticipants]);

  const acceptFile = (selected) => {
    if (!selected) return;
    if (!/\.(xlsx|xls|csv)$/i.test(selected.name || "")) {
      toast.error("Choose an Excel or CSV file.");
      return;
    }
    setFile(selected);
    setResult(null);
  };

  const upload = async () => {
    if (!file || !projectId) return;
    const requestedProjectId = projectId;
    const controller = new AbortController();
    uploadControllerRef.current = controller;
    setUploading(true);
    setResult(null);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await axiosInstance.post(
        `/api/projects/${encodeURIComponent(requestedProjectId)}/event_participants/import`, data,
        { signal: controller.signal },
      );
      if (projectIdRef.current !== requestedProjectId) return;
      setCurrentPage(1);
      if (currentPage === 1) await loadParticipants();
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      setShowImport(false);
      toast.success(response.data?.message || "Participants imported successfully.");
    } catch (error) {
      if (error?.code === "ERR_CANCELED") return;
      const data = error.response?.data;
      const message = data?.error || data?.message || "Unable to import participants.";
      setResult({ errors: data?.errors || [], error: message });
      toast.error(message);
    } finally {
      if (uploadControllerRef.current === controller) uploadControllerRef.current = null;
      setUploading(false);
    }
  };

  const exportParticipants = async () => {
    if (!projectId || exporting) return;
    setExporting(true);
    try {
      const response = await axiosInstance.get(
        `/api/projects/${encodeURIComponent(projectId)}/event_participants/export`,
        { params: { search, ...(nfcStatus ? { nfc_configured: nfcStatus } : {}) }, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "JECON-event-participants.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to export participants.");
    } finally {
      setExporting(false);
    }
  };

  return <main className={`table-container equipment-container equipment-page participants-import-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
    <div className="equipment-page-content participants-import-content">
      <header className="equipment-page-header participants-page-header">
        <div className="equipment-header-titleblock"><h2 className="page-title">Event Participants</h2></div>
        <div className="participants-header-actions">
          <label className="participants-search" aria-label="Search participants"><FiSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search participants..." /></label>
          <div className={`participants-status-filter${isNfcMenuOpen ? " is-open" : ""}`} ref={nfcMenuRef}><button type="button" className="participants-status-trigger" aria-haspopup="listbox" aria-expanded={isNfcMenuOpen} onClick={() => setIsNfcMenuOpen((open) => !open)} onKeyDown={(event) => { if (event.key === "Escape") setIsNfcMenuOpen(false); }}><span className="participants-filter-label">NFC</span><strong>{nfcStatus === "true" ? "Configured" : nfcStatus === "false" ? "Not configured" : "All"}</strong><FiChevronDown aria-hidden="true" /></button>{isNfcMenuOpen && <div className="participants-status-menu" role="listbox" aria-label="Filter by NFC status">{[["", "All"], ["true", "Configured"], ["false", "Not configured"]].map(([value, label]) => <button key={value || "all"} type="button" role="option" aria-selected={nfcStatus === value} className={nfcStatus === value ? "selected" : ""} onClick={() => { setNfcStatus(value); setCurrentPage(1); setIsNfcMenuOpen(false); }}>{label}</button>)}</div>}</div>
          <button type="button" className="participants-refresh-button" onClick={() => loadParticipants()} disabled={!projectId || loading} title="Refresh participants" aria-label="Refresh participants"><FiRefreshCw className={loading ? "is-spinning" : ""} /></button>
          <button type="button" className="participants-export-button" onClick={exportParticipants} disabled={!projectId || exporting} title="Export participants with JECON and Bazan branding"><FiDownload />{exporting ? "Exporting…" : "Export"}</button>
          <button type="button" className="participants-import-submit" onClick={() => setShowImport(true)} disabled={!projectId || uploading}>Import participants</button>
        </div>
      </header>

      {!projectId && <div className="participants-import-alert">Select a project from the sidebar to continue.</div>}
      {projectId && <section className={`equipment-table-card participants-list ${loading ? "loading" : ""}`}>
        {loadError && <p className="error">{loadError}</p>}
        {loading && <div className="participants-table-skeleton" aria-label="Loading participants">{Array.from({ length: 6 }, (_, index) => <div className="participants-skeleton-row" key={index}><span /><span /><span /><span /><span /></div>)}</div>}
        {!loadError && !loading && !participants.length && <p className="participants-empty">No participants match your search.</p>}
        {!loading && !!participants.length && <div className="equipment-table-wrapper participants-table-wrap"><table className="equipment-data-table participants-data-table"><thead><tr><th className="participants-col-sr">Sr. No.</th><th>Name</th><th>Designation</th><th>Category</th><th>NFC</th></tr></thead><tbody>{participants.map((participant, index) => <tr key={participant.participant_id}><td>{((currentPage - 1) * itemsPerPage) + index + 1}</td><td>{participant.name || "—"}</td><td>{participant.designation || "—"}</td><td>{participant.category || "—"}</td><td>{participant.nfc?.configured ? "Configured" : "Not assigned"}</td></tr>)}</tbody></table></div>}
        {!loading && totalRecords > 0 && <PaginationFooter totalRecords={totalRecords} currentPage={currentPage} itemsPerPage={itemsPerPage} setCurrentPage={setCurrentPage} setItemsPerPage={setItemsPerPage} />}
      </section>}
    </div>

    {showImport && <div className="participants-modal-overlay" onClick={() => !uploading && setShowImport(false)} role="presentation"><section className="participants-modal" role="dialog" aria-modal="true" aria-labelledby="participants-import-title" onClick={(event) => event.stopPropagation()}><header className="participants-modal-header"><div><h2 id="participants-import-title">Upload Excel</h2></div><button type="button" className="participants-modal-close" aria-label="Close import dialog" onClick={() => setShowImport(false)} disabled={uploading}>×</button></header><div className="participants-modal-body"><p className="participants-import-format"><strong>Required columns:</strong> Passport (or Passport no.), Employee Name, Designation<br /><small>Optional column: Category</small></p><div className={`participants-drop-zone ${isDragging ? "dragging" : ""}`} onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={(event) => { event.preventDefault(); setIsDragging(false); }} onDrop={(event) => { event.preventDefault(); setIsDragging(false); acceptFile(event.dataTransfer.files?.[0]); }}><strong>📂 Drag &amp; drop your Excel file here</strong><span>or</span><div className="participants-file-picker"><button type="button" className="participants-import-file-button" onClick={() => inputRef.current?.click()} disabled={uploading}>Choose File</button><span>{file ? file.name : "No file chosen"}</span></div></div><input ref={inputRef} className="participants-import-input" type="file" accept=".xlsx,.xls,.csv" onChange={(event) => acceptFile(event.target.files?.[0])} />{result && <section className="participants-import-result" aria-live="polite">{result.error ? <p className="error">{result.error}</p> : null}{!!result.errors?.length && <details><summary>{result.errors.length} row issue{result.errors.length === 1 ? "" : "s"}</summary><ul>{result.errors.map((issue, index) => <li key={`${issue}-${index}`}>{issue}</li>)}</ul></details>}</section>}</div><footer className="participants-modal-footer"><button type="button" className="participants-import-submit" onClick={upload} disabled={uploading || !file}>{uploading && <span className="participants-button-spinner" />} {uploading ? "Importing…" : "Import"}</button><button type="button" className="participants-modal-cancel" onClick={() => setShowImport(false)} disabled={uploading}>Cancel</button></footer></section></div>}
  </main>;
}
